import React, { useState } from 'react';
import {
  FileText,
  Search,
  Filter,
  Download,
  Calendar,
  User,
  Clock,
  Heart,
  Activity,
  Stethoscope,
  FileImage,
  TestTube,
  ShieldAlert,
  ChevronRight
} from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { useMedicalHistory } from '../hooks/use-medical-history';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import GlassCard from '@/components/ui/glass-card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import Loading from '@/components/ui/Loading';
import ErrorMessage from '@/components/ui/ErrorMessage';
import { EmptyState } from '@/components/ui/EmptyState';
import PageContainer from '@/components/layout/PageContainer';
import ErrorBoundary from '@/components/ui/ErrorBoundary';
import { cn } from "@/lib/utils";

/**
 * Medical Records Page Component
 * Displays comprehensive medical history with timeline view, filtering, and document viewer
 */
const MedicalRecords: React.FC = () => {
  const {
    timelineEvents,
    conditions,
    allergies,
    surgeries,
    documents,
    categories,
    isLoading,
    error,
    filters,
    setFilters
  } = useMedicalHistory();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'timeline' | 'categories'>('timeline');

  // Format date safely
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'MMM d, yyyy') : 'Invalid Date';
    } catch {
      return 'Invalid Date';
    }
  };

  // Get category icon
  const getCategoryIcon = (category: string) => {
    const iconMap: Record<string, JSX.Element> = {
      'conditions': <Heart className="h-4 w-4" />,
      'allergies': <ShieldAlert className="h-4 w-4" />,
      'surgeries': <Stethoscope className="h-4 w-4" />,
      'medications': <TestTube className="h-4 w-4" />,
      'lab-results': <TestTube className="h-4 w-4" />,
      'imaging': <FileImage className="h-4 w-4" />,
      'checkups': <Activity className="h-4 w-4" />,
      'documents': <FileText className="h-4 w-4" />
    };
    return iconMap[category] || <FileText className="h-4 w-4" />;
  };

  // Get severity badge
  const getSeverityBadge = (severity?: string) => {
    if (!severity) return null;

    const severityMap: Record<string, { variant: "default" | "destructive" | "warning", label: string }> = {
      'low': { variant: 'default', label: 'Low' },
      'medium': { variant: 'warning', label: 'Medium' },
      'high': { variant: 'destructive', label: 'High' },
    };

    const config = severityMap[severity] || { variant: 'default' as const, label: severity };

    return (
      <Badge variant={config.variant} className="capitalize">
        {config.label}
      </Badge>
    );
  };

  // Handle filter changes
  const handleTimeRangeChange = (value: string) => {
    setFilters({ ...filters, timeRange: value });
  };

  const handleCategoryChange = (value: string) => {
    if (value === 'all') {
      setFilters({ ...filters, categories: [] });
    } else {
      const currentCategories = filters.categories || [];
      const newCategories = currentCategories.includes(value)
        ? currentCategories.filter(c => c !== value)
        : [...currentCategories, value];
      setFilters({ ...filters, categories: newCategories });
    }
  };

  // Handle export
  const handleExport = () => {
    const csvContent = [
      ['Date', 'Title', 'Description', 'Category', 'Provider', 'Severity'].join(','),
      ...timelineEvents.map(event => [
        formatDate(event.date),
        `"${event.title}"`,
        `"${event.description}"`,
        event.category || 'N/A',
        event.provider || 'N/A',
        event.severity || 'N/A'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `medical-records-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <PageContainer
      title="Medical Records"
      description="View your complete medical history"
      icon={<FileText className="h-5 w-5" />}
      actions={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      }
    >
      <ErrorBoundary>
        {/* Filters */}
        <GlassCard padding="lg" variant="default" className="mb-6 shadow-sm border-white/40">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-2 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  type="text"
                  placeholder="Search medical records..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setFilters({ ...filters, searchTerm: e.target.value });
                  }}
                  className="pl-10"
                />
              </div>

              {/* Time Range */}
              <Select value={filters.timeRange} onValueChange={handleTimeRangeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Time Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-time">All Time</SelectItem>
                  <SelectItem value="last-month">Last Month</SelectItem>
                  <SelectItem value="last-6-months">Last 6 Months</SelectItem>
                  <SelectItem value="last-year">Last Year</SelectItem>
                </SelectContent>
              </Select>

              {/* View Mode */}
              <div className="flex gap-2">
                <Button
                  variant={viewMode === 'timeline' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('timeline')}
                  className="flex-1"
                >
                  <Clock className="h-4 w-4 mr-1" />
                  Timeline
                </Button>
                <Button
                  variant={viewMode === 'categories' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('categories')}
                  className="flex-1"
                >
                  <Filter className="h-4 w-4 mr-1" />
                  Categories
                </Button>
              </div>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2 mt-4">
              {categories.map(category => (
                <Badge
                  key={category.id}
                  variant={filters.categories?.includes(category.id) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => handleCategoryChange(category.id)}
                >
                  {getCategoryIcon(category.id)}
                  <span className="ml-1">{category.label}</span>
                </Badge>
              ))}
            </div>
        </GlassCard>

        {/* Content */}
        {viewMode === 'timeline' ? (
          <TimelineView
            events={timelineEvents}
            isLoading={isLoading}
            error={error}
            formatDate={formatDate}
            getCategoryIcon={getCategoryIcon}
            getSeverityBadge={getSeverityBadge}
            onSelectEvent={setSelectedEvent}
          />
        ) : (
          <CategoriesView
            conditions={conditions}
            allergies={allergies}
            surgeries={surgeries}
            documents={documents}
            isLoading={isLoading}
            error={error}
            formatDate={formatDate}
            getSeverityBadge={getSeverityBadge}
          />
        )}

        {/* Event Detail Modal */}
        {selectedEvent && (
          <EventDetailModal
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            formatDate={formatDate}
            getCategoryIcon={getCategoryIcon}
            getSeverityBadge={getSeverityBadge}
          />
        )}
      </ErrorBoundary>
    </PageContainer>
  );
};

/**
 * Timeline View Component
 */
interface TimelineViewProps {
  events: any[];
  isLoading: boolean;
  error: Error | null;
  formatDate: (date: string | null | undefined) => string;
  getCategoryIcon: (category: string) => JSX.Element;
  getSeverityBadge: (severity?: string) => JSX.Element | null;
  onSelectEvent: (event: any) => void;
}

const TimelineView: React.FC<TimelineViewProps> = ({
  events,
  isLoading,
  error,
  formatDate,
  getCategoryIcon,
  getSeverityBadge,
  onSelectEvent
}) => {
  return (
    <GlassCard padding="none" variant="default" className="shadow-sm border-white/40 overflow-hidden">
      <div className="p-6 border-b border-white/20 dark:border-white/10 bg-white/40 dark:bg-slate-800/40">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Medical History Timeline</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Your medical events in chronological order
        </p>
      </div>
      <div className="p-6">
        <ErrorBoundary>
          {isLoading ? (
            <Loading text="Loading medical history..." />
          ) : error ? (
            <ErrorMessage message={error?.message || "Failed to load medical history."} />
          ) : events.length === 0 ? (
            <EmptyState
              title="No Medical Records Found"
              message="Try adjusting your filters."
              className="py-10"
            />
          ) : (
            <ScrollArea className="h-[600px] pr-4">
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />

                {/* Timeline events */}
                <div className="space-y-8">
                  {events.map((event) => (
                    <div key={event.id} className="relative pl-14">
                      {/* Timeline dot */}
                      <div
                        className={cn(
                          "absolute left-4 w-4 h-4 rounded-full border-2 bg-background",
                          event.severity === 'high' && "border-destructive",
                          event.severity === 'medium' && "border-warning",
                          event.severity === 'low' && "border-primary"
                        )}
                      />

                      {/* Event card */}
                      <GlassCard
                        padding="lg"
                        variant="subtle"
                        className="cursor-pointer border-white/40 hover:shadow-md transition-all duration-300 hover:translate-y-[-2px]"
                        onClick={() => onSelectEvent(event)}
                      >
                        <div>
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              {event.category && getCategoryIcon(event.category)}
                              <h3 className="font-semibold">{event.title}</h3>
                            </div>
                            <div className="flex gap-2">
                              {getSeverityBadge(event.severity)}
                              <ChevronRight className="h-5 w-5 text-muted-foreground" />
                            </div>
                          </div>

                          <p className="text-sm text-muted-foreground mb-2">
                            {event.description}
                          </p>

                          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(event.date)}
                            </span>
                            {event.provider && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {event.provider}
                              </span>
                            )}
                          </div>
                        </div>
                      </GlassCard>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollArea>
          )}
        </ErrorBoundary>
      </div>
    </GlassCard>
  );
};

/**
 * Categories View Component
 */
interface CategoriesViewProps {
  conditions: any[];
  allergies: any[];
  surgeries: any[];
  documents: any[];
  isLoading: boolean;
  error: Error | null;
  formatDate: (date: string | null | undefined) => string;
  getSeverityBadge: (severity?: string) => JSX.Element | null;
}

const CategoriesView: React.FC<CategoriesViewProps> = ({
  conditions,
  allergies,
  surgeries,
  documents,
  isLoading,
  error,
  formatDate,
  getSeverityBadge
}) => {
  return (
    <Tabs defaultValue="conditions" className="w-full">
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="conditions">
          <Heart className="h-4 w-4 mr-2" />
          Conditions
        </TabsTrigger>
        <TabsTrigger value="allergies">
          <ShieldAlert className="h-4 w-4 mr-2" />
          Allergies
        </TabsTrigger>
        <TabsTrigger value="surgeries">
          <Stethoscope className="h-4 w-4 mr-2" />
          Surgeries
        </TabsTrigger>
        <TabsTrigger value="documents">
          <FileText className="h-4 w-4 mr-2" />
          Documents
        </TabsTrigger>
      </TabsList>

      <TabsContent value="conditions">
        <GlassCard padding="none" variant="default" className="shadow-sm border-white/40 overflow-hidden">
          <div className="p-6 border-b border-white/20 dark:border-white/10 bg-white/40 dark:bg-slate-800/40">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Medical Conditions</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Your diagnosed medical conditions</p>
          </div>
          <div className="p-6">
            <ErrorBoundary>
              {isLoading ? (
                <Loading text="Loading conditions..." />
              ) : error ? (
                <ErrorMessage message="Failed to load conditions." />
              ) : conditions.length === 0 ? (
                <EmptyState title="No Conditions" message="No medical conditions on record." />
              ) : (
                <div className="space-y-4">
                  {conditions.map(condition => (
                    <ConditionCard
                      key={condition.id}
                      condition={condition}
                      formatDate={formatDate}
                      getSeverityBadge={getSeverityBadge}
                    />
                  ))}
                </div>
              )}
            </ErrorBoundary>
          </div>
        </GlassCard>
      </TabsContent>

      <TabsContent value="allergies">
        <GlassCard padding="none" variant="default" className="shadow-sm border-white/40 overflow-hidden">
          <div className="p-6 border-b border-white/20 dark:border-white/10 bg-white/40 dark:bg-slate-800/40">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Allergies</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Your known allergies</p>
          </div>
          <div className="p-6">
            <ErrorBoundary>
              {isLoading ? (
                <Loading text="Loading allergies..." />
              ) : error ? (
                <ErrorMessage message="Failed to load allergies." />
              ) : allergies.length === 0 ? (
                <EmptyState title="No Allergies" message="No allergies on record." />
              ) : (
                <div className="space-y-4">
                  {allergies.map(allergy => (
                    <ConditionCard
                      key={allergy.id}
                      condition={allergy}
                      formatDate={formatDate}
                      getSeverityBadge={getSeverityBadge}
                    />
                  ))}
                </div>
              )}
            </ErrorBoundary>
          </div>
        </GlassCard>
      </TabsContent>

      <TabsContent value="surgeries">
        <GlassCard padding="none" variant="default" className="shadow-sm border-white/40 overflow-hidden">
          <div className="p-6 border-b border-white/20 dark:border-white/10 bg-white/40 dark:bg-slate-800/40">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Surgeries & Procedures</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Your surgical history</p>
          </div>
          <div className="p-6">
            <ErrorBoundary>
              {isLoading ? (
                <Loading text="Loading surgeries..." />
              ) : error ? (
                <ErrorMessage message="Failed to load surgeries." />
              ) : surgeries.length === 0 ? (
                <EmptyState title="No Surgeries" message="No surgical procedures on record." />
              ) : (
                <div className="space-y-4">
                  {surgeries.map(surgery => (
                    <ConditionCard
                      key={surgery.id}
                      condition={surgery}
                      formatDate={formatDate}
                      getSeverityBadge={getSeverityBadge}
                    />
                  ))}
                </div>
              )}
            </ErrorBoundary>
          </div>
        </GlassCard>
      </TabsContent>

      <TabsContent value="documents">
        <GlassCard padding="none" variant="default" className="shadow-sm border-white/40 overflow-hidden">
          <div className="p-6 border-b border-white/20 dark:border-white/10 bg-white/40 dark:bg-slate-800/40">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Medical Documents</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Your medical documents and reports</p>
          </div>
          <div className="p-6">
            <ErrorBoundary>
              {isLoading ? (
                <Loading text="Loading documents..." />
              ) : error ? (
                <ErrorMessage message="Failed to load documents." />
              ) : documents.length === 0 ? (
                <EmptyState title="No Documents" message="No medical documents on record." />
              ) : (
                <div className="space-y-3">
                  {documents.map(doc => (
                    <DocumentCard key={doc.id} document={doc} formatDate={formatDate} />
                  ))}
                </div>
              )}
            </ErrorBoundary>
          </div>
        </GlassCard>
      </TabsContent>
    </Tabs>
  );
};

/**
 * Condition Card Component
 */
interface ConditionCardProps {
  condition: any;
  formatDate: (date: string | null | undefined) => string;
  getSeverityBadge: (severity?: string) => JSX.Element | null;
}

const ConditionCard: React.FC<ConditionCardProps> = ({ condition, formatDate, getSeverityBadge }) => {
  return (
    <GlassCard padding="lg" variant="subtle" className="border-white/20 shadow-sm hover:shadow-md transition-shadow">
      <div>
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-semibold">{condition.name}</h3>
          <div className="flex gap-2">
            <Badge variant={condition.status === 'active' ? 'default' : 'secondary'}>
              {condition.status}
            </Badge>
            {getSeverityBadge(condition.severity)}
          </div>
        </div>

        <p className="text-sm text-muted-foreground mb-3">{condition.description}</p>

        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
          <div>
            <span className="font-medium">Diagnosed:</span> {formatDate(condition.diagnosisDate)}
          </div>
          {condition.location && (
            <div>
              <span className="font-medium">Location:</span> {condition.location}
            </div>
          )}
        </div>

        {condition.symptoms && condition.symptoms.length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-medium text-muted-foreground mb-1">Symptoms:</p>
            <div className="flex flex-wrap gap-1">
              {condition.symptoms.map((symptom: string, idx: number) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {symptom}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </GlassCard>
  );
};

/**
 * Document Card Component
 */
interface DocumentCardProps {
  document: any;
  formatDate: (date: string | null | undefined) => string;
}

const DocumentCard: React.FC<DocumentCardProps> = ({ document, formatDate }) => {
  return (
    <GlassCard padding="lg" variant="subtle" className="border-white/20 hover:shadow-md transition-shadow cursor-pointer">
      <div>
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <FileText className="h-8 w-8 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium truncate">{document.title}</h4>
            <p className="text-xs text-muted-foreground">{document.provider}</p>
            <p className="text-xs text-muted-foreground">{formatDate(document.date)}</p>
          </div>
          <Button variant="ghost" size="sm">
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </GlassCard>
  );
};

/**
 * Event Detail Modal Component
 */
interface EventDetailModalProps {
  event: any;
  onClose: () => void;
  formatDate: (date: string | null | undefined) => string;
  getCategoryIcon: (category: string) => JSX.Element;
  getSeverityBadge: (severity?: string) => JSX.Element | null;
}

const EventDetailModal: React.FC<EventDetailModalProps> = ({
  event,
  onClose,
  formatDate,
  getCategoryIcon,
  getSeverityBadge
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <GlassCard padding="none" variant="default" className="w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4 shadow-2xl border-white/40" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-white/20 dark:border-white/10 bg-white/40 dark:bg-slate-800/40">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-2">
              {event.category && getCategoryIcon(event.category)}
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white">{event.title}</h3>
            </div>
            <div className="flex gap-2">
              {getSeverityBadge(event.severity)}
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">{event.description}</p>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-muted-foreground">Date</p>
                <p>{formatDate(event.date)}</p>
              </div>
              {event.provider && (
                <div>
                  <p className="font-medium text-muted-foreground">Provider</p>
                  <p>{event.provider}</p>
                </div>
              )}
              {event.location && (
                <div>
                  <p className="font-medium text-muted-foreground">Location</p>
                  <p>{event.location}</p>
                </div>
              )}
            </div>

            {event.details && event.details.length > 0 && (
              <div>
                <p className="font-medium text-muted-foreground mb-2">Details</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {event.details.map((detail: string, idx: number) => (
                    <li key={idx}>{detail}</li>
                  ))}
                </ul>
              </div>
            )}

            {event.attachments && event.attachments.length > 0 && (
              <div>
                <p className="font-medium text-muted-foreground mb-2">Attachments</p>
                <div className="space-y-2">
                  {event.attachments.map((attachment: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between p-2 border rounded">
                      <span className="text-sm">{attachment.name}</span>
                      <Button variant="ghost" size="sm">
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </GlassCard>
    </div>
  );
};

// Missing import
import { X } from 'lucide-react';

export default MedicalRecords;
